import { useState, useMemo, useCallback } from 'react';
import { ProductionRow, ProductionStats } from './types';

export const useProductionData = (data: ProductionRow[]): ProductionStats => {
  return useMemo(() => {
    const parseHours = (hours: any): number => {
      const parsed = parseFloat(hours);
      return isNaN(parsed) ? 0 : parsed;
    };

    const parseReels = (reels: any): number => {
      const parsed = parseInt(reels || '0', 10);
      return isNaN(parsed) ? 0 : parsed;
    };

    const totalHours = data.reduce((sum, item) => sum + parseHours(item.productionHours), 0);
    const uniqueTitles = new Set(data.map(item => item.title)).size;
    const totalReels = data.reduce((sum, item) => sum + parseReels(item.comp_Reel), 0);

    // Full status counts (useful for detailed breakdowns)
    const statusCounts = data.reduce((acc, item) => {
      const status = item.status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Individual counts
    const completedCount = statusCounts['Completed'] || statusCounts['Complete'] || 0;
    const inProgressCount = statusCounts['In Progress'] || 0;

    // QC stages – adjust the exact string if your data uses different naming
    const qc1Count = statusCounts['QC 1'] || 0;
    const qc2Count = statusCounts['QC 2'] || 0;
    const qc3Count = statusCounts['QC 3'] || 0;

    // Total in any QC stage
    const totalQualityCheckCount = qc1Count + qc2Count + qc3Count;

    // Employee, daily, task aggregations (unchanged)
    const employeeHours = data.reduce((acc, item) => {
      const name = item.emp_Name || 'Unknown';
      acc[name] = (acc[name] || 0) + parseHours(item.productionHours);
      return acc;
    }, {} as Record<string, number>);

    const dailyProduction = data.reduce((acc, item) => {
      const date = item.date || 'Unknown';
      acc[date] = (acc[date] || 0) + parseHours(item.productionHours);
      return acc;
    }, {} as Record<string, number>);

    const taskTypes = data.reduce((acc, item) => {
      const task = item.task || 'Unknown';
      acc[task] = (acc[task] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const completionRate = data.length > 0 
      ? ((completedCount / data.length) * 100).toFixed(1) 
      : '0.0';

    const avgHoursPerTask = data.length > 0
      ? (totalHours / data.length).toFixed(2)
      : '0.00';

    // QC progress percentage (overall)
    const qualityCheckRate = data.length > 0
      ? ((totalQualityCheckCount / data.length) * 100).toFixed(1)
      : '0.0';

    return {
      totalHours: totalHours.toFixed(1),
      uniqueTitles,
      totalReels,
      completed: completedCount,
      inProgress: inProgressCount,
      // Individual QC stages
      qc1: qc1Count,
      qc2: qc2Count,
      qc3: qc3Count,
      // Total in Quality Check (any stage)
      qualityCheck: totalQualityCheckCount,
      qualityCheckRate: `${qualityCheckRate}%`,
      // Full breakdown if needed elsewhere
      statusCounts,
      employeeHours,
      dailyProduction,
      taskTypes,
      avgHoursPerTask,
      completionRate: `${completionRate}%`,
      totalTasks: data.length,
    };
  }, [data]);
};
export const useFilter = (
  data: ProductionRow[],
  initialFilter: string = 'all'
) => {
  const [filter, setFilter] = useState(initialFilter);

  const filteredData = useMemo(() => {
    if (filter === 'all') {
      return data;
    }

    // Combined filter for all Quality Check stages
    if (filter === 'Quality Check') {
      return data.filter((item) =>
        ['QC 1', 'QC 2', 'QC 3'].includes(item.status || '')
      );
    }
    
    // Exact match for any other status (In Progress, Completed, QC 1, etc.)
    return data.filter((item) => item.status === filter);
  }, [data, filter]);

  return [filteredData, filter, setFilter] as const;
};

export const useSort = (data: ProductionRow[], initialKey: keyof ProductionRow = 'date') => {
  const [sortConfig, setSortConfig] = useState<{ key: keyof ProductionRow; direction: 'asc' | 'desc' }>({
    key: initialKey,
    direction: 'desc',
  });

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const requestSort = useCallback((key: keyof ProductionRow) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  return [sortedData, requestSort, sortConfig] as const;
};